import { useState } from 'react'
import { FlatList, Pressable, Text, View } from 'react-native'
import { useGlobalSearchParams, useRouter } from 'expo-router'
import { bignumber } from 'mathjs'
import { useTranslation } from 'react-i18next'
import { SheetManager } from 'react-native-actions-sheet'

import { BuyOrderStatus, SellOrderStatus } from '@/constants/common'
import type { OrderListParams } from '@/types/common'
import { cn } from '@/utils/cn'
import {
  getClosedBuyStatusFromFilter,
  getClosedSellStatusFromFilter,
  getOpenBuyStatusFromFilter,
  getOpenSellStatusFromFilter
} from '@/utils/order'
import { ExpandMore, ReceiptOff, Spinner } from '@/components/icon'
import OrderCard from '@/components/OrderCard'
import RefreshControl from '@/components/RefreshControl'

interface OrderListProps {
  status: 'open' | 'closed'
  action: 'buy' | 'sell'
  filter: string
  isLoading: boolean
  data: any
  refetch: () => void
  onFetchMore: () => void
  isFetchingMore: boolean
}

function OrderList({
  status,
  action,
  filter,
  isLoading,
  data: rawData,
  refetch,
  onFetchMore,
  isFetchingMore
}: OrderListProps) {
  const router = useRouter()
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  // console.log({ action, status, filter })

  async function handleRefetch() {
    setIsRefreshing(true)
    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }

  if (isLoading && !rawData) {
    return (
      <View className={'h-full items-center justify-center bg-background-secondary'}>
        <View className={'animate-spin'}>
          <Spinner className={'size-6 text-primary'} />
        </View>
      </View>
    )
  }

  if (!rawData) {
    return null
  }

  const data = action === 'buy' ? getBuyOrders(rawData) : getSellOrders(rawData, filter, status)

  return (
    <FlatList
      contentContainerStyle={{ flexGrow: 1, padding: 16, gap: 16 }}
      data={data}
      keyExtractor={(item) => item.id}
      onEndReached={onFetchMore}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefetch} />}
      renderItem={({ item: order, index }) => {
        // HACK
        const actualDealAmount =
          action === 'buy' || 'childId' in order ? undefined : bignumber(order.actualDealAmount.value)
        const dealAmount =
          action === 'buy' || 'childId' in order
            ? bignumber(order.dealAmount.value)
            : bignumber(order.expectDealAmount.value)

        const actionable = isActionable(action, order.status)

        return (
          <>
            <OrderCard
              id={order.id}
              orderType={action}
              orderStatus={action === 'buy' ? order.status : getSellOrderStatus(!('childId' in order), order.status)}
              actualDealAmount={actualDealAmount}
              dealAmount={dealAmount}
              basePrice={order.basePrice}
              createdAt={action === 'sell' && 'childId' in order ? order.pairedAt || order.createdAt : order.createdAt}
              isActionable={actionable}
              onPress={() => {
                if (action === 'buy') {
                  router.push({ pathname: '/order/buy/[id]', params: { id: order.internalKey } })
                } else {
                  router.push({
                    pathname: '/order/sell/[id]',
                    params: {
                      id: order.internalKey,
                      childId: order.childId
                    }
                  })
                }
              }}
            />
            {isFetchingMore && index === data.length - 1 && (
              <View className={'mx-auto mt-4 animate-spin'}>
                <Spinner className={'size-6 text-primary'} />
              </View>
            )}
          </>
        )
      }}
      ListHeaderComponent={() => <ListHeader status={status} />}
      ListEmptyComponent={<EmptyList />}
    />
  )
}

export default OrderList

function ListHeader({ status }: { status: OrderListProps['status'] }) {
  const { t } = useTranslation()
  const router = useRouter()
  const { filter = 'all', action = 'buy' } = useGlobalSearchParams<OrderListParams>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  return (
    <View className={'flex-row'}>
      <Pressable
        className={cn(
          'my-[-8px] flex-row items-center gap-x-1 rounded-lg px-2 py-1.5',
          isDrawerOpen && 'bg-fill-subtle'
        )}
        onPress={() => {
          setIsDrawerOpen(true)
          SheetManager.show('open-filter', {
            payload: {
              type: `${status}-${action}-order`,
              value: filter,
              setValue: (filter) => router.setParams({ filter }),
              closeDrawer: () => setIsDrawerOpen(false)
            }
          })
        }}
      >
        <Text className={'text-xs font-medium text-text-secondary'}>
          {t('orders:status', { status: t(`orderList:tags.${action}.${filter}`) })}
        </Text>
        <ExpandMore className={'size-4 text-text-secondary'} />
      </Pressable>
      <View className={'flex-1'} />
    </View>
  )
}

function EmptyList() {
  const { t } = useTranslation()
  return (
    <View className={'mb-[-16px] flex-1 items-center justify-center'}>
      <View className={'items-center gap-4'}>
        <ReceiptOff className={'size-20 text-text-quaternary'} />
        <Text className={'text-sm text-text-secondary'}>{t('orders:noOrders')}</Text>
      </View>
    </View>
  )
}

function getBuyOrders(data: any[]) {
  return data.map((d) => d.node)
}

function getSellOrders(data: any[], filter: string, status: 'open' | 'closed') {
  let newData = []
  for (let i = 0; i < data.length; i++) {
    newData.push(data[i].node)
    if (data[i].node.pairBuyOrder.length !== 0) {
      newData = [
        ...newData,
        ...data[i].node.pairBuyOrder
          .filter(
            (p: Record<string, any>) =>
              ![BuyOrderStatus.completed, BuyOrderStatus.forceCanceled, BuyOrderStatus.canceled].includes(p.status)
          )
          .map((p: Record<string, any>) => ({
            ...p,
            internalKey: data[i].node.internalKey,
            childId: p.internalKey
          }))
      ]
    }
  }

  return newData.filter((item) => {
    const filteredSellStatus =
      status === 'open' ? getOpenSellStatusFromFilter(filter) : getClosedSellStatusFromFilter(filter)
    const filteredBuyStatus =
      status === 'open' ? getOpenBuyStatusFromFilter(filter) : getClosedBuyStatusFromFilter(filter)
    const orderStatus = getSellOrderStatus(!('childId' in item), item.status)
    if ('childId' in item) {
      return filteredBuyStatus.includes(orderStatus)
    }
    return filteredSellStatus.includes(orderStatus)
  })
}

function getSellOrderStatus(isParentOrder: boolean, status: SellOrderStatus): number {
  // console.log({ action, isParentOrder, status })
  if (!isParentOrder) {
    return status
  }
  switch (status) {
    case SellOrderStatus.unpaired:
    case SellOrderStatus.paired:
    case SellOrderStatus.paymentMade:
    case SellOrderStatus.complainted:
    case SellOrderStatus.appealing:
      return 1
    case SellOrderStatus.completed:
    case SellOrderStatus.requestCancel:
    case SellOrderStatus.canceling:
    case SellOrderStatus.canceled:
    case SellOrderStatus.forceCanceled:
      return status
    default:
      return 1
  }
}

function isActionable(action: 'buy' | 'sell', status: BuyOrderStatus) {
  if (action === 'buy') {
    return status === BuyOrderStatus.paired || status === BuyOrderStatus.complainted
  }
  return status === BuyOrderStatus.paymentMade
}
